import React from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  addDays,
  classRuns,
  classSpan,
  diffDays,
  formatDay,
  formatISO,
  formatRange,
  parseDate,
} from "./plannerUtils";
import { colorFor } from "./plannerConstants";

export default function TermTimeline({
  classes,
  conflictIds,
  semester,
  today,
  selectedId,
  instructorNames,
  onSelect,
}) {
  const { startDate, endDate } = semester;
  const total = diffDays(startDate, endDate) + 1;
  const pct = (d) => Math.min(100, Math.max(0, (diffDays(startDate, d) / total) * 100));

  const months = [];
  const cursor = parseDate(startDate);
  cursor.setDate(1);
  while (formatISO(cursor) <= endDate) {
    const a = formatISO(cursor);
    const b = formatISO(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));
    const s = a < startDate ? startDate : a;
    const e = b > endDate ? endDate : b;
    months.push({
      name: formatDay(a, { month: "long" }),
      left: pct(s),
      width: pct(addDays(e, 1)) - pct(s),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const mondays = [];
  for (let d = startDate; d <= endDate; d = addDays(d, 1)) {
    if (parseDate(d).getDay() === 1) mondays.push(d);
  }

  const categories = [...new Set(classes.map((c) => c.category || "Other"))];
  const groups = categories.map((cat) => ({
    cat,
    rows: classes
      .filter((c) => (c.category || "Other") === cat && classSpan(c))
      .sort((a, b) => classSpan(a).start.localeCompare(classSpan(b).start)),
  }));

  const todayInRange = today >= startDate && today <= endDate;

  return (
    <div className="sp-tv" data-testid="planner-term-view">
      <div className="sp-tv-ticks">
        {mondays.map((d, i) => (
          <span key={d} style={{ left: `${pct(d)}%` }}>
            <em>W{i + 1}</em>
          </span>
        ))}
        {todayInRange && (
          <b className="sp-tv-today" style={{ left: `${pct(today)}%` }}>
            <em>Today</em>
          </b>
        )}
      </div>
      <div className="sp-tv-r sp-tv-hd">
        <span />
        <div className="sp-tv-tr">
          {months.map((m) => (
            <span
              key={`${m.name}-${m.left}`}
              className="sp-tv-m"
              style={{ left: `${m.left}%`, width: `${m.width}%` }}
            >
              {m.name}
            </span>
          ))}
        </div>
      </div>
      {groups
        .filter((g) => g.rows.length)
        .map((g) => (
          <React.Fragment key={g.cat}>
            <div className="sp-tv-g">
              <i style={{ background: colorFor(g.cat) }} />
              {g.cat}
              <em>{g.rows.length}</em>
            </div>
            {g.rows.map((sc) => {
              const span = classSpan(sc);
              const cf = conflictIds.has(sc.id);
              const instructor = instructorNames[sc.instructorId];
              return (
                <div
                  key={sc.id}
                  className={`sp-tv-r${selectedId === sc.id ? " on" : ""}`}
                  onClick={() => onSelect(sc.id)}
                  data-testid={`planner-term-row-${sc.id}`}
                >
                  <div className="sp-tv-l">
                    <b>{sc.title}</b>
                    <span>{instructor || "No instructor"}</span>
                  </div>
                  <div className="sp-tv-tr">
                    {classRuns(sc).map((run, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`sp-bar tb${selectedId === sc.id ? " on" : ""}${cf ? " cf" : ""}${sc.instructorId ? "" : " ua"}`}
                        style={{
                          "--c": colorFor(sc.category),
                          left: `${pct(run.start)}%`,
                          width: `max(10px, ${pct(addDays(run.end, 1)) - pct(run.start)}%)`,
                        }}
                        title={formatRange(run.start, run.end)}
                        aria-label={`${sc.title}, ${formatRange(run.start, run.end)}${sc.instructorId ? "" : ", no instructor"}${cf ? ", scheduling conflict" : ""}`}
                      >
                        {cf && i === 0 && <WarningAmberIcon sx={{ fontSize: 12 }} />}
                      </button>
                    ))}
                    <span
                      className="sp-tv-d sp-mo"
                      style={{ left: `calc(${pct(addDays(span.end, 1))}% + 8px)` }}
                    >
                      {formatRange(span.start, span.end)}
                    </span>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      {!groups.some((g) => g.rows.length) && (
        <p className="sp-emp">No classes scheduled yet.</p>
      )}
    </div>
  );
}
